<template>
  <el-dialog
    v-el-drag-dialog
    :visible.sync="innerVisible"
    :title="$t('SeaExport.SelectContainerInfo')"
    width="800px"
    :close-on-click-modal="false"
    class="app-main-inner"
  >
    <cargo-ag-grid
      :show-side-bar="false"
      :grid-options="gridOptions"
      :column-defs="columnDefs"
      :row-data="rowData"
      style="width:100%;height:300px"
      @gridReady="params=>{gridApi=params.api}"
    />
    <span slot="footer">
      <el-button no="Confirm" type="primary" @click="saveHandler">{{ $t('SeaExport.Confirm') }}</el-button>
      <el-button no="Cancel" @click="innerVisible=false">{{ $t('SeaExport.Cancel') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import CargoAgGridCommonInputEditor from '@/components/Cargo/Common/CargoAgGridCommonInputEditor'
import ColumnUtils from '@/utils/ColumnUtils'
import { DischargingTruckingContainerUsage } from '@/class/model/booking/DischargingTruckingContainerUsage'
import { queryAvailableDischargingContainerUsageByJob, batchAddDischargingTruckingContainerUsages } from '@/api/seaExport/dischargingTrucking'

export default {
  name: 'DischargingTruckingContainerUsageLinkDialog',
  props: {
    visible: { type: Boolean, required: true },
    jobUuid: { type: String, required: true },
    truckingInfoUuid: { type: String, required: true },
    serialNos: { type: Array, required: true }
  },
  data() {
    return {
      innerVisible: false,
      containerUsages: [],
      gridOptions: {
        frameworkComponents: {
          CargoAgGridCommonInputEditor
        },
        singleClickEdit: true,
        rowSelection: 'multiple',
        suppressCellSelection: true,
        defaultColDef: {
          resizable: true
        },
        context: {
          componentParent: this
        }
      },
      columnDefs: [
        {
          headerName: '',
          field: '',
          width: 65,
          headerCheckboxSelection: true,
          checkboxSelection: true,
          sortable: false,
          suppressMenu: true,
          suppressSizeToFit: true,
          resizable: false
        },
        {
          headerName: this.$t('SeaExport.ContainerSize'),
          field: 'eqSize',
          width: 70,
          cellRenderer: (params) => {
            return ColumnUtils.containerUsageFiledLabelFunction(params.data, 'eqSize')
          }
        },
        {
          headerName: this.$t('SeaExport.ContainerType'),
          field: 'eqType',
          width: 70,
          cellRenderer: (params) => {
            return ColumnUtils.containerUsageFiledLabelFunction(params.data, 'eqType')
          }
        },
        {
          headerName: this.$t('SeaExport.ContainerNo'),
          field: 'containerNo',
          width: 100,
          cellRenderer: (params) => {
            return ColumnUtils.containerUsageFiledLabelFunction(params.data, 'containerNo')
          }
        },
        {
          headerName: this.$t('SeaExport.ContainerWeight'),
          field: 'containerWeight',
          width: 100,
          cellRenderer: (params) => {
            return ColumnUtils.containerUsageFiledLabelFunction(params.data, 'containerWeight')
          }
        },
        {
          headerName: this.$t('SeaExport.SealNo'),
          field: 'sealNo',
          width: 100,
          cellRenderer: (params) => {
            return ColumnUtils.containerUsageFiledLabelFunction(params.data, 'sealNo')
          }
        },
        {
          headerName: this.$t('SeaExport.Quantity'),
          field: 'quantity',
          width: 70,
          cellRenderer: (params) => {
            return ColumnUtils.numberColumnLabelFunc(params.value)
          },
          editable: true,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: {
            isNumber: true,
            inputProps: {
              precision: 0,
              min: 0,
              max: 999999999
            }
          }
        },
        {
          headerName: this.$t('SeaExport.GrossWeight'),
          field: 'weight',
          width: 100,
          cellRenderer: (params) => {
            return ColumnUtils.number3ColumnLabelFunc(params.value)
          },
          editable: true,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: {
            isNumber: true,
            inputProps: {
              precision: 3,
              min: 0,
              max: 999999999
            }
          }
        },
        {
          headerName: this.$t('SeaExport.Cubage'),
          field: 'cubage',
          width: 100,
          cellRenderer: (params) => {
            return ColumnUtils.number3ColumnLabelFunc(params.value)
          },
          editable: true,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: {
            isNumber: true,
            inputProps: {
              precision: 3,
              min: 0,
              max: 999999999
            }
          }
        }
      ],
      rowData: [],
      gridApi: null
    }
  },
  watch: {
    visible(newVal) {
      this.innerVisible = newVal
      if (newVal) {
        this.queryHandler()
      }
    },
    innerVisible(newVal) {
      if (!newVal) {
        this.containerUsages = []
        this.rowData = []
      }
      this.$emit('update:visible', newVal)
    }
  },
  created() {
    if (this.visible) {
      this.innerVisible = true
      this.queryHandler()
    }
  },
  methods: {
    queryHandler() {
      queryAvailableDischargingContainerUsageByJob({ jobUuid: this.jobUuid }).then(res => {
        this.containerUsages = res.data
        this.rowData = []
        for (const item of this.containerUsages) {
          this.rowData.push({
            containerUsageUuid: item.uuid,
            quantity: item.quantity,
            maxQuantity: item.quantity,
            weight: item.weight,
            maxWeight: item.weight,
            cubage: item.cubage,
            maxCubage: item.cubage,
            container: item.container,
            serialNo: item.serialNo
          })
        }
      })
    },
    saveHandler() {
      const rows = []
      const nodes = this.gridApi.rowModel.rowsToDisplay
      for (const node of nodes) {
        if (node.selected) {
          node.data.rowNum = node.rowIndex + 1
          rows.push(node.data)
        }
      }
      if (rows.length === 0) {
        return
      }
      const truckingUsages = []
      for (const row of rows) {
        const quantity = Number(row.quantity) || 0
        if (quantity <= 0) {
          this.$message.warning(this.$t('SeaExport.LineXQuantityMustGreaterThanZero', { rowNum: row.rowNum }))
          return
        }
        if (quantity > row.maxQuantity) {
          this.$message.warning(this.$t('SeaExport.LineXQuantityExceedsMaxValue', { rowNum: row.rowNum, quantity, maxValue: row.maxQuantity }))
          return
        }
        const weight = Number(row.weight) || 0
        if (weight <= 0) {
          this.$message.warning(this.$t('SeaExport.LineXWeightMustGreaterThanZero', { rowNum: row.rowNum }))
          return
        }
        if (weight > row.maxWeight) {
          this.$message.warning(this.$t('SeaExport.LineXWeightExceedsMaxValue', { rowNum: row.rowNum, weight, maxValue: row.maxWeight }))
          return
        }
        const cubage = Number(row.cubage) || 0
        if (cubage <= 0) {
          this.$message.warning(this.$t('SeaExport.LineXCubageMustGreaterThanZero', { rowNum: row.rowNum }))
          return
        }
        if (cubage > row.maxCubage) {
          this.$message.warning(this.$t('SeaExport.LineXCubageExceedsMaxValue', { rowNum: row.rowNum, cubage, maxValue: row.maxCubage }))
          return
        }
        const truckingUsage = new DischargingTruckingContainerUsage().fillDefaultValue().getData()
        truckingUsage.dischargingTruckingUuid = this.truckingInfoUuid
        truckingUsage.containerUsageUuid = row.containerUsageUuid
        truckingUsage.quantity = quantity
        truckingUsage.weight = weight
        truckingUsage.cubage = cubage
        truckingUsages.push(truckingUsage)
        if (row.serialNo) {
          this.serialNos.push(String(row.serialNo))
        }
      }
      const serialNo = this.serialNos.join(',')
      batchAddDischargingTruckingContainerUsages(truckingUsages, { serialNo }).then(() => {
        this.innerVisible = false
        this.$emit('selectContainerUsageHandler', serialNo)
      })
    }
  }
}
</script>
